// src/pages/Appli/AppliAdmin/Prospects/components/KanbanProspectsDnD.jsx
import React, { useMemo, useState, useCallback } from "react";
import ButtonEdit from "../../../../../components/Button/ButtonEdit";
import { InlineProspectStatutSelect } from "./InlineProspectStatutSelect";

const colTone = {
  "Nouveau Prospect": "from-blue-50 to-blue-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "A Recontacter": "from-violet-50 to-violet-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "Contacté & en attente": "from-amber-50 to-amber-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "Prospect Qualifié": "from-orange-50 to-orange-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "En Test": "from-green-50 to-green-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "Client": "from-emerald-50 to-emerald-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "Client SAAS": "from-teal-50 to-teal-100 dark:from-slate-800/40 dark:to-slate-800/20",
  "Perdu": "from-slate-50 to-slate-100 dark:from-slate-800/40 dark:to-slate-800/20",
};

export function KanbanProspectsDnD({
  data = [],
  statutsOrder = [],
  onMove,   // (prospect, newStatut) => void
  onEdit,   // (prospect) => void
}) {
  // group by statut
  const columns = useMemo(() => {
    const groups = Object.fromEntries(statutsOrder.map((s) => [s, []]));
    for (const p of data) {
      const key = statutsOrder.includes(p.statut) ? p.statut : statutsOrder[0];
      groups[key].push(p);
    }
    for (const k of Object.keys(groups)) {
      groups[k].sort((a, b) =>
        `${a.nom || ""} ${a.prenom || ""}`.localeCompare(`${b.nom || ""} ${b.prenom || ""}`, "fr")
      );
    }
    return groups;
  }, [data, statutsOrder]);

  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const findProspect = useCallback(
    (id) => data.find((p) => String(p.id) === String(id)),
    [data]
  );

  const onDragStart = (e, p) => {
    e.dataTransfer.setData("text/plain", String(p.id));
    e.dataTransfer.effectAllowed = "move";
    setDragId(String(p.id));
  };

  const onDragOverCol = (e, colKey) => {
    e.preventDefault();
    setOverCol(colKey);
    e.dataTransfer.dropEffect = "move";
  };

  const onDragLeaveCol = (e, colKey) => {
    if (overCol === colKey) setOverCol(null);
  };

  const onDropCol = (e, colKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const p = findProspect(id);
    setOverCol(null);
    setDragId(null);
    if (!p || p.statut === colKey) return;
    onMove?.(p, colKey);
  };

  return (
    <div className="flex gap-4 overflow-x-auto p-4">
      {statutsOrder.map((colKey) => (
        <div
          key={colKey}
          className="w-80 flex-shrink-0 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
          onDragOver={(e) => onDragOverCol(e, colKey)}
          onDragLeave={(e) => onDragLeaveCol(e, colKey)}
          onDrop={(e) => onDropCol(e, colKey)}
        >
          <div className={`px-4 py-3 border-b border-slate-200 dark:border-slate-800 font-semibold rounded-t-2xl bg-gradient-to-r ${colTone[colKey] || ""}`}>
            {colKey} <span className="text-slate-500">({columns[colKey]?.length || 0})</span>
          </div>

          <div className={`p-3 min-h-[200px] transition ${overCol === colKey ? "ring-2 ring-violet-400/70 rounded-xl" : ""}`}>
            {columns[colKey]?.length ? (
              <div className="space-y-3">
                {columns[colKey].map((p) => (
                  <KanbanCard
                    key={p.id}
                    p={p}
                    dragId={dragId}
                    onDragStart={onDragStart}
                    statutsOrder={statutsOrder}
                    onMove={onMove}
                    onEdit={onEdit}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-400 text-center py-8">Glissez un prospect ici</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function KanbanCard({ p, dragId, onDragStart, statutsOrder, onMove, onEdit }) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);
  const detailsId = `kanban-prospect-details-${p.id}`;

  return (
    <article
      draggable
      onDragStart={(e) => onDragStart(e, p)}
      className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 p-3 shadow-sm transition ${
        String(dragId) === String(p.id) ? "opacity-60" : ""
      }`}
    >
      {/* Header compact */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{p.nom} {p.prenom}</div>
          <div className="text-xs text-slate-500 truncate">
            {p.association?.nom_asso || p.Association?.nom_asso || "—"}
            {p.fonction ? ` • ${p.fonction}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ButtonEdit onClick={(e) => { e.stopPropagation?.(); onEdit?.(p); }} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation?.(); toggle(); }}
            aria-expanded={open}
            aria-controls={detailsId}
            className="px-2 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
            title={open ? "Replier" : "Dérouler"}
          >
            {open ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {/* Zone déroulée (statut + infos) */}
      <div
        id={detailsId}
        className={`transition-all duration-200 ease-out ${open ? "mt-3 max-h-[1000px]" : "max-h-0"} overflow-hidden`}
      >
        {/* Statut inline */}
        <div className="mb-2">
          <InlineProspectStatutSelect
            value={p.statut || ""}
            options={statutsOrder}
            onChange={(v) => onMove?.(p, v)}
            size="sm"
          />
          <span className="ml-2 text-[11px] text-slate-500">Astuce : glissez la carte pour changer de colonne.</span>
        </div>

        {/* Détails */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Info label="Téléphone" value={p.telephone || "—"} />
          <Info label="Email" value={p.email || "—"} />
          <Info label="Provenance" value={p.provenance || "—"} />
          <Info label="Intérêt" value={p.interet_produit?.nom_app || "—"} />
          <Info
            className="col-span-2"
            label="Contact"
            value={p.date_contact ? new Date(p.date_contact).toLocaleDateString("fr-FR") : "—"}
          />
        </div>
      </div>
    </article>
  );
}

function Info({ label, value, className = "" }) {
  return (
    <div className={`rounded-lg bg-white dark:bg-slate-900/60 p-2 border border-slate-200 dark:border-slate-800 ${className}`}>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-medium text-xs">{value}</div>
    </div>
  );
}
